import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { directoryApi, type ClientDirectoryEntry } from "@/lib/api";
import { formatNaira } from "@/lib/mock-data";
import { Plus, Search, Pencil, Trash2, Mail, Phone, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

type ClientStatus = ClientDirectoryEntry["status"];

const statusTone: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
  pending: "bg-warning/15 text-warning-foreground border-warning/30",
};

type Form = { name: string; email: string; phone: string; status: ClientStatus };
const empty: Form = { name: "", email: "", phone: "", status: "active" };

export default function ClientsPage() {
  const token = useAuth((s) => s.session?.accessToken);
  // A corporate login manages its own contacts here too (list_clients/
  // create_client/update_client are scoped server-side to their own
  // corporate_account_id) but can't touch status or delete — those stay
  // staff/admin-only server-side (update_client rejects a status change,
  // delete_client is require_admin_claims).
  const isCorporate = useAuth((s) => s.session?.kind === "corporate");
  const [items, setItems] = useState<ClientDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | ClientStatus>("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ClientDirectoryEntry | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [del, setDel] = useState<ClientDirectoryEntry | null>(null);

  useEffect(() => {
    let cancelled = false;
    directoryApi
      .clients(token)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => toast.error("Failed to load clients"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(() => items.filter((c) => {
    if (status !== "all" && c.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      c.name.toLowerCase().includes(s) ||
      (c.email?.toLowerCase().includes(s) ?? false) ||
      (c.phone?.includes(s) ?? false)
    );
  }), [items, q, status]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c: ClientDirectoryEntry) => {
    setEditing(c);
    setForm({ name: c.name, email: c.email ?? "", phone: c.phone ?? "", status: c.status });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email required");
    try {
      if (editing) {
        const updated = await directoryApi.updateClient(editing.id, {
          full_name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          status: isCorporate ? undefined : form.status,
        }, token);
        setItems((cur) => cur.map((c) => (c.id === editing.id ? updated : c)));
        toast.success("Client updated");
      } else {
        const created = await directoryApi.createClient({
          full_name: form.name,
          email: form.email,
          phone: form.phone || undefined,
        }, token);
        setItems((cur) => [created, ...cur]);
        toast.success("Client added");
      }
      setOpen(false);
    } catch {
      toast.error(editing ? "Could not update client" : "Could not add client");
    }
  };
  const remove = async () => {
    if (!del) return;
    try {
      await directoryApi.deleteClient(del.id, token);
      setItems((cur) => cur.filter((c) => c.id !== del.id));
      toast.success(`Removed ${del.name}`);
    } catch {
      toast.error("Could not remove client");
    } finally {
      setDel(null);
    }
  };

  const counts = {
    total: items.length,
    active: items.filter((c) => c.status === "active").length,
    suspended: items.filter((c) => c.status === "suspended").length,
    pending: items.filter((c) => c.status === "pending").length,
  };

  return (
    <>
      <Topbar
        title="Clients"
        subtitle={`${counts.total} accounts · ${counts.active} active`}
        actions={<Button size="sm" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Add client</Button>}
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-4">
          {(["total", "active", "pending", "suspended"] as const).map((k) => (
            <div key={k} className="surface-card p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div>
              <div className="mt-1 text-2xl font-bold">{counts[k]}</div>
            </div>
          ))}
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email…" className="pl-9" />
            </div>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Shipments</TableHead>
                <TableHead>Spend</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6}><LoadingBlock label="Loading clients…" /></TableCell></TableRow>
              ) : (
                <>
                  {filtered.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-[11px] text-muted-foreground">{c.id} · joined {new Date(c.created_at).toLocaleDateString()}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3 text-muted-foreground" />{c.email ?? "—"}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{c.phone ?? "—"}</div>
                      </TableCell>
                      <TableCell>{c.total_shipments}</TableCell>
                      <TableCell>{formatNaira(c.total_spent_kobo / 100)}</TableCell>
                      <TableCell>
                        <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", statusTone[c.status] ?? "bg-muted text-muted-foreground border-border")}>
                          {c.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" asChild aria-label="Dashboard"><Link to={`/app/users/clients/${c.id}`}><LayoutDashboard className="h-3.5 w-3.5" /></Link></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                        {isCorporate ? null : (
                          <Button variant="ghost" size="icon" onClick={() => setDel(c)} aria-label="Delete" className="text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No clients match.</TableCell></TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "Add client"}</DialogTitle>
            <DialogDescription>Personal account for retail shipping.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            {isCorporate ? null : (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as ClientStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This deactivates the account and removes access.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
